<template>
  <LayerSatellite />
  <LayerOrthophotoOutdoor
    v-if="filteredOrthophotos.length && hasOrthophotoAccess"
    :before="layerIdRealEstate"
    :layerId="layerIdOrthophotoOutdoor"
  />
  <LayerOrthophotoOutdoorTiles
    v-if="filteredOrthophotos.length && hasOrthophotoAccess"
    :before="layerIdRealEstate"
    :layerId="layerIdOrthophotoOutdoorTiles"
  />
  <LayerDrawingOutdoor
    v-if="filteredDrawings.length && hasDrawingAccess"
    :before="layerIdRealEstate"
    :layerId="layerIdDrawingOutdoor"
  />
  <LayerDrawingOutdoorTiles
    v-if="filteredDrawings.length && hasDrawingAccess"
    :before="layerIdRealEstate"
    :layerId="layerIdDrawingOutdoorTiles"
  />
  <LayerRealEstate :source="realEstateSource" :layerIdRealEstate="layerIdRealEstate" />
  <!-- LayerVectorTile is a frontend demo for AH Umeå -->
  <LayerVectorTile
    v-if="realmId === 'akademiskahus' && mapVectorTileState !== 'hidden'"
    :before="layerIdBuilding"
    :layerId="layerIdVectorTile"
  />
  <LayerWms v-if="filteredWmsLayers.length" :before="layerIdRealEstate" :layerId="layerIdWms" />
  <LayerLandZone
    v-if="buildingLayerCreated && shouldShowLandZones && hasZoneAccess"
    :before="isPoiLayerCreated"
  />
  <LayerLineOutdoor
    v-if="buildingLayerCreated && shouldShowOutdoorLines && hasLineReaderAccess"
    :before="isPoiLayerCreated"
  />
  <LayerPoiOutdoorMarker
    v-if="hasPoiReaderAccess && buildingLayerCreated && !isDrawPointActive"
    :before="layerIdBuilding"
    :layerId="layerIdPoiOutdoorMarker"
    :layerIdSelected="layerIdPoiOutdoorMarkerSelected"
    @poiLayerCreated="poiLayerCreated = true"
    @poiLayerRemoved="poiLayerCreated = false"
  />
  <!-- If binding :withinLayerId="layerIdRealEstate" to LayerDrawPoint, the borders include all real estates and functionality breaks -->
  <LayerDrawPoint
    v-if="isDrawPointActive"
    :pointSource="fullPoi"
    @submitPoint="handleDrawPoint"
    @submitPointMobile="handleDrawPointMobile"
  />
  <LayerBuilding
    v-if="buildingSource && layerAboveBuilding"
    :before="layerAboveBuilding"
    :layerId="layerIdBuilding"
    :source="buildingSource"
  />

  <template v-for="layer in orderedLayers" :key="layer.order">
    <component
      :is="layer.component"
      v-if="layer.visible"
      :opacity="layer.opacity"
      :before="layer.before"
    />
  </template>

  <LayerMarkerBuilding
    v-if="buildingSource"
    :layerIdMarker="layerIdMarkerBuilding"
    :buildingSource="buildingSource"
    :complexSource="complexSource"
  />
  <LayerBuildingMarkerCluster
    v-if="buildingSource"
    :layerId="layerIdBuildingMarkerCluster"
    :buildingSource="buildingSource"
    :complexSource="complexSource"
  />
  <LayerComplexMarker
    v-if="complexSource"
    :layerId="layerIdComplexMarker"
    :source="complexSource"
    :buildingSource="buildingSource"
  />
  <LayerComplexMarkerCluster v-if="complexSource" :source="complexSource" />
  <LayerMarkerTickets v-if="showFeatureErrands && !isDrawPointActive" />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { MAPVIEW_PREFIX } from '@/store/module-prefixes'
import { RESET_CURRENT_TOOL, SET_DRAW_POINT_FEATURE } from '@/store/mutation-types'
import {
  LayerBuilding,
  LayerBuildingMarkerCluster,
  LayerComplexMarker,
  LayerComplexMarkerCluster,
  LayerDrawPoint,
  LayerDrawingOutdoor,
  LayerDrawingOutdoorTiles,
  LayerLandZone,
  LayerLineOutdoor,
  LayerMarkerBuilding,
  LayerMarkerTickets,
  LayerOrthophotoOutdoor,
  LayerOrthophotoOutdoorTiles,
  LayerPoiOutdoorMarker,
  LayerRealEstate,
  LayerSatellite,
  LayerVectorTile,
  LayerWms
} from './index'
import { getRealmId } from '@/utils/realmId'
import { storeToRefs } from 'pinia'
import { useLayers } from '@/pinia/layers'
import { GeoJSONSourceSpecification } from 'mapbox-gl'

// Props definition
defineProps<{
  complexSource: object | null
  realEstateSource: object | null
  buildingSource: GeoJSONSourceSpecification | null
}>()

const store = useStore()
const generalStore = useGeneralStore()
const { setCurrentOverlayModalPinia, setRightClickCoordinate, setShowOverlayModalPinia } =
  generalStore

const layerIdBuilding = ref('layer-building')
const layerIdBuildingMarkerCluster = ref('layer-building-marker-cluster')
const layerIdComplexMarker = ref('layer-complex-marker')
const layerIdDrawingOutdoor = ref('layer-drawing-outdoor')
const layerIdDrawingOutdoorTiles = ref('layer-drawing-outdoor-tiles')
const layerIdMarkerBuilding = ref('layer-marker-building')
const layerIdOrthophotoOutdoor = ref('layer-orthophoto-outdoor')
const layerIdOrthophotoOutdoorTiles = ref('layer-orthophoto-outdoor-tiles')
const layerIdPoiOutdoorMarker = ref('layer-poi-outdoor-marker')
const layerIdPoiOutdoorMarkerSelected = ref('layer-poi-outdoor-marker-selected')
const layerIdRealEstate = ref('layer-real-estate')
const layerIdVectorTile = ref('layer-vector-tile')
const layerIdWms = ref('layer-wms')
const poiLayerCreated = ref(false)

const {
  filteredDrawings,
  filteredLineLayers,
  filteredOrthophotos,
  filteredWmsLayers,
  showFeatureErrands
} = storeToRefs(generalStore)

const { orderedLayers } = storeToRefs(useLayers())

const hasDrawingAccess = computed(() => store.getters.hasDrawingAccess)
const hasLineReaderAccess = computed(() => store.getters.hasLineReaderAccess)
const hasOrthophotoAccess = computed(() => store.getters.hasOrthophotoAccess)
const hasPoiReaderAccess = computed(() => store.getters.hasPoiReaderAccess)
const hasZoneAccess = computed(() => store.getters.hasZoneAccess)
const isDrawPointActive = computed(() => store.getters.isDrawPointActive)

const mountedMapLayerIds = computed(() => store.state.mountedMapLayerIds)
const selectedPoi = computed(() => store.state.selectedPoi)

const mapVectorTileState = computed(() => store.state[MAPVIEW_PREFIX].mapVectorTileState)
const filteredMapLayers = computed(() => store.state[MAPVIEW_PREFIX].filteredLayers)
const outdoorPoisForSelectedRealEstate = computed(
  () => store.state[MAPVIEW_PREFIX].outdoorPoisForSelectedRealEstate
)

const fullPoi = computed(() => {
  if (selectedPoi.value) {
    return outdoorPoisForSelectedRealEstate.value.find(
      (poi: any) => poi.properties.guid === selectedPoi.value.guid
    )
  }
  return null
})

const isPoiLayerCreated = computed(() => {
  if (poiLayerCreated.value) {
    if (
      mountedMapLayerIds.value.find((id: string) => id === layerIdPoiOutdoorMarkerSelected.value)
    ) {
      return layerIdPoiOutdoorMarkerSelected.value
    }
    return layerIdPoiOutdoorMarker.value
  }
  return layerIdBuilding.value
})

const realmId = computed(() => getRealmId())

const shouldShowLandZones = computed(() => {
  return filteredMapLayers.value.some((layer: any) =>
    layer.types.some((type: any) => type.visible === true)
  )
})

const shouldShowOutdoorLines = computed(() => {
  return filteredLineLayers.value.some((layer: any) =>
    layer.types.some((type: any) => type.visible === true)
  )
})

const layerAboveBuilding = computed(() => {
  return mountedMapLayerIds.value.find(
    (id: string) => id === layerIdMarkerBuilding.value || id === layerIdComplexMarker.value
  )
})

const buildingLayerCreated = computed(() => {
  return mountedMapLayerIds.value.includes(layerIdBuilding.value)
})

const resetTool = () => {
  store.commit(RESET_CURRENT_TOOL)
}

const setDrawPointFeature = (point: any) => {
  store.commit(SET_DRAW_POINT_FEATURE, { point })
}

const handleDrawPoint = (point: any) => {
  setDrawPointFeature(point)
  resetTool()
}

const handleDrawPointMobile = (point: any, editMode: boolean) => {
  if (!point) return
  const rightClickCoordinate = point.geometry.coordinates
  if (!editMode) setRightClickCoordinate(rightClickCoordinate)
  setCurrentOverlayModalPinia('createAssets')
  setShowOverlayModalPinia(true)
}
</script>
